import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Alert
} from '@mui/material';
import {
  Phone as PhoneIcon,
  NotificationsActive as AlertIcon,
  SupportAgent as RecruiterIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

/**
 * PhonePromptModal
 * Low-friction modal to collect phone number after career analysis completes.
 * Positioned as a value-add, not a gate.
 */
export default function PhonePromptModal({ open, onClose, onSave, existingPhone = '' }) {
  const [phone, setPhone] = useState(existingPhone);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const formatPhone = (value) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
    setError('');
  };

  const validatePhone = () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validatePhone()) return;
    
    setSaving(true);
    try {
      await onSave(phone);
      onClose();
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const benefits = [
    {
      icon: <RecruiterIcon sx={{ color: '#00fff5', fontSize: 20 }} />,
      text: 'Direct recruiter outreach opportunities'
    },
    {
      icon: <AlertIcon sx={{ color: '#00fff5', fontSize: 20 }} />,
      text: 'SMS alerts when matching jobs appear'
    },
    {
      icon: <CheckIcon sx={{ color: '#00fff5', fontSize: 20 }} />,
      text: 'Priority support and faster responses'
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0a0a0a',
          border: '1px solid #262626',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PhoneIcon sx={{ color: '#00fff5' }} />
          <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 600 }}>
            Complete Your Profile
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography sx={{ color: '#a3a3a3', mb: 3 }}>
          Add your phone number to unlock exclusive opportunities:
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {benefits.map((benefit, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {benefit.icon}
              <Typography sx={{ color: '#fafafa' }}>{benefit.text}</Typography>
            </Box>
          ))}
        </Box>
        
        <TextField
          fullWidth
          label="Phone Number"
          placeholder="(555) 123-4567"
          value={phone}
          onChange={handlePhoneChange}
          error={!!error}
          helperText={error}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon sx={{ color: '#525252' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#fafafa',
              bgcolor: '#141414',
              '& fieldset': { borderColor: '#262626' },
              '&:hover fieldset': { borderColor: '#404040' },
              '&.Mui-focused fieldset': { borderColor: '#00fff5' },
            },
            '& .MuiInputLabel-root': { color: '#737373' },
            '& .MuiFormHelperText-root': { color: '#ef4444' }
          }}
        />
        
        <Alert 
          severity="info" 
          sx={{ 
            mt: 2, 
            bgcolor: 'rgba(0,255,245,0.05)', 
            color: '#a3a3a3',
            border: '1px solid rgba(0,255,245,0.1)',
            '& .MuiAlert-icon': { color: '#00fff5' }
          }}
        >
          We never share your phone number without permission. Recruiters can only 
          contact you if you approve the opportunity first.
        </Alert>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          sx={{ color: '#737373' }}
        >
          Skip for Now
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !phone}
          sx={{
            bgcolor: '#00fff5',
            color: '#0a0a0a',
            fontWeight: 600,
            '&:hover': { bgcolor: '#00ccc4' },
            '&:disabled': { bgcolor: '#404040', color: '#737373' }
          }}
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

